/**
 * Pure geometry for the hand-rolled panel engine. No React, no DOM. Every
 * drag/resize/keyboard mutation routes through these functions so min/max
 * constraints and canvas bounds are enforced in exactly one place and are
 * trivially unit-testable.
 */
import type { CanvasBounds, PanelConstraints, PanelDescriptor, PanelPosition, PanelSize } from "./types";

/** Keyboard nudge step (px). Matches the visible grid feel and stays touch-sane. */
export const KEYBOARD_MOVE_STEP = 16;
export const KEYBOARD_RESIZE_STEP = 16;

function clamp(value: number, low: number, high: number): number {
  if (high < low) return low;
  return Math.min(Math.max(value, low), high);
}

export function clampSize(size: PanelSize, constraints: PanelConstraints): PanelSize {
  const maxWidth = constraints.maxWidth ?? Number.POSITIVE_INFINITY;
  const maxHeight = constraints.maxHeight ?? Number.POSITIVE_INFINITY;
  return {
    width: clamp(size.width, constraints.minWidth, maxWidth),
    height: clamp(size.height, constraints.minHeight, maxHeight),
  };
}

export function clampPosition(
  position: PanelPosition,
  size: PanelSize,
  bounds?: CanvasBounds | null,
): PanelPosition {
  if (bounds === undefined || bounds === null) {
    return { x: Math.max(0, position.x), y: Math.max(0, position.y) };
  }
  return {
    x: clamp(position.x, 0, Math.max(0, bounds.width - size.width)),
    y: clamp(position.y, 0, Math.max(0, bounds.height - size.height)),
  };
}

/** Absolute move (pointer drag). Position is clamped to the canvas. */
export function movePanelTo(
  panel: PanelDescriptor,
  position: PanelPosition,
  bounds?: CanvasBounds | null,
): PanelDescriptor {
  return { ...panel, position: clampPosition(position, panel.size, bounds) };
}

/** Relative move (arrow keys). */
export function movePanelBy(
  panel: PanelDescriptor,
  dx: number,
  dy: number,
  bounds?: CanvasBounds | null,
): PanelDescriptor {
  return movePanelTo(panel, { x: panel.position.x + dx, y: panel.position.y + dy }, bounds);
}

function boundSize(
  size: PanelSize,
  position: PanelPosition,
  constraints: PanelConstraints,
  bounds?: CanvasBounds | null,
): PanelSize {
  const constrained = clampSize(size, constraints);
  if (bounds === undefined || bounds === null) return constrained;
  // Never let a panel resize past the canvas edge, but never below its min size.
  const capped: PanelSize = {
    width: Math.min(constrained.width, Math.max(constraints.minWidth, bounds.width - position.x)),
    height: Math.min(constrained.height, Math.max(constraints.minHeight, bounds.height - position.y)),
  };
  return clampSize(capped, constraints);
}

/** Absolute resize (pointer handle). */
export function resizePanelTo(
  panel: PanelDescriptor,
  size: PanelSize,
  bounds?: CanvasBounds | null,
): PanelDescriptor {
  return { ...panel, size: boundSize(size, panel.position, panel.constraints, bounds) };
}

/** Relative resize (Shift + arrow keys). */
export function resizePanelBy(
  panel: PanelDescriptor,
  dw: number,
  dh: number,
  bounds?: CanvasBounds | null,
): PanelDescriptor {
  return resizePanelTo(
    panel,
    { width: panel.size.width + dw, height: panel.size.height + dh },
    bounds,
  );
}
